import { useState, useCallback, useRef, useEffect } from 'react';
import { calculateDateRange, getPresetDateRange, getTaskDates, MIN_SCALE, MAX_SCALE } from '../utils';

/**
 * Hook for managing Gantt view presets (Today, This Week, This Month, etc.)
 * 
 * BUTTER-SMOOTH ANIMATION:
 * - Cache timeline elements before animation (no DOM queries during animation)
 * - Pre-calculate all constants before animation loop
 * - Pure linear interpolation for consistent, jitter-free motion
 * - Single React state update only at animation end
 * - Longer duration (450ms) for smoother perceived motion
 */
export const useGanttPresets = (chartRef, tasks, setZoomScale, currentZoomScale) => {
  const [viewPreset, setViewPreset] = useState('all');
  const [extraRange, setExtraRange] = useState(null);
  
  const animationRef = useRef(null);
  const currentScaleRef = useRef(currentZoomScale);
  // Cache timeline elements to avoid querySelectorAll during animation
  const cachedElementsRef = useRef(null);
  
  // Keep ref in sync
  useEffect(() => {
    currentScaleRef.current = currentZoomScale;
  }, [currentZoomScale]);

  const handlePresetChange = useCallback((presetId) => {
    if (!chartRef.current) return;
    
    setViewPreset(presetId);
    
    // Cancel ongoing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    
    const chart = chartRef.current;
    const sidebarWidth = 200;
    // Read viewport width ONCE before animation
    const viewportWidth = chart.clientWidth - sidebarWidth;
    const msPerDay = 86400000; // 24 * 60 * 60 * 1000
    
    // Get date range
    const currentDateRange = calculateDateRange(tasks, null, extraRange);
    if (!currentDateRange) return;
    
    const { totalDays, startDate: rangeStartDate } = currentDateRange;
    const rangeStartMs = rangeStartDate.getTime();
    
    // Current state - read all DOM values ONCE
    const startScale = currentScaleRef.current;
    const startTimelineWidth = Math.max(800, totalDays * 40 * startScale);
    const startPxPerMs = startTimelineWidth / (totalDays * msPerDay);
    const startScrollLeft = chart.scrollLeft;
    
    // Current viewport in ms
    const startLeftMs = rangeStartMs + (startScrollLeft / startPxPerMs);
    const startRightMs = startLeftMs + (viewportWidth / startPxPerMs);
    
    // Target state
    let targetLeftMs, targetRightMs, targetScale;
    let newExtraRange = null;
    
    if (presetId === 'all') {
      let minDate = null, maxDate = null;
      
      tasks.forEach(task => {
        const dates = getTaskDates(task);
        if (!dates) return;
        if (!minDate || dates.startDate < minDate) minDate = dates.startDate;
        if (!maxDate || dates.endDate > maxDate) maxDate = dates.endDate;
      });
      
      if (!minDate || !maxDate) {
        const today = Date.now();
        targetLeftMs = today - 7 * msPerDay;
        targetRightMs = today + 7 * msPerDay;
        targetScale = 2;
      } else {
        targetLeftMs = minDate.getTime() - msPerDay / 2;
        targetRightMs = maxDate.getTime() + msPerDay / 2;
        const targetDays = (targetRightMs - targetLeftMs) / msPerDay;
        targetScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, viewportWidth / (targetDays * 40)));
      }
    } else {
      const preset = getPresetDateRange(presetId);
      if (!preset) return;
      
      targetLeftMs = preset.startDate.getTime();
      targetRightMs = preset.endDate.getTime();
      const presetDays = (targetRightMs - targetLeftMs) / msPerDay;
      targetScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, viewportWidth / (presetDays * 40)));
      newExtraRange = { startDate: preset.startDate, endDate: preset.endDate };
    }
    
    // Set extra range (may expand timeline)
    setExtraRange(newExtraRange);
    
    // Pre-calculate date range for animation (avoid recalculating each frame)
    const animDateRange = calculateDateRange(tasks, null, newExtraRange);
    if (!animDateRange) return;
    
    const animTotalDays = animDateRange.totalDays;
    const animRangeStartMs = animDateRange.startDate.getTime();
    
    // Cache timeline elements BEFORE animation starts (single DOM query)
    cachedElementsRef.current = chart.querySelectorAll(
      '.gantt-timeline-header__timeline, .gantt-row__timeline, .gantt-swimlane__header-timeline'
    );
    
    // Pre-calculate animation deltas (avoid subtraction in hot loop)
    const deltaLeftMs = targetLeftMs - startLeftMs;
    const deltaRightMs = targetRightMs - startRightMs;
    
    // Animation - longer duration for smoother perception
    const duration = 450;
    const startTime = performance.now();
    
    // Track last values to avoid unnecessary DOM writes
    let lastWidth = -1;
    let lastScroll = -1;
    
    const animate = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / duration);
      
      // Pure linear interpolation - smoothest possible motion
      const currentLeftMs = startLeftMs + deltaLeftMs * t;
      const currentRightMs = startRightMs + deltaRightMs * t;
      
      // Calculate scale from current viewport span
      const spanMs = currentRightMs - currentLeftMs;
      const spanDays = spanMs / msPerDay;
      const scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, viewportWidth / (spanDays * 40)));
      
      // Calculate new timeline width
      const timelineWidth = Math.max(800, animTotalDays * 40 * scale);
      
      // Calculate scroll position
      const pxPerMs = timelineWidth / (animTotalDays * msPerDay);
      const scrollLeft = (currentLeftMs - animRangeStartMs) * pxPerMs;
      const maxScroll = Math.max(0, timelineWidth + sidebarWidth - viewportWidth - sidebarWidth);
      const clampedScroll = Math.max(0, Math.min(maxScroll, scrollLeft));
      
      // Batch DOM writes - only if values changed significantly (reduces micro-jitter)
      const roundedWidth = Math.round(timelineWidth);
      const roundedScroll = Math.round(clampedScroll);
      
      if (roundedWidth !== lastWidth) {
        const widthStr = `${roundedWidth}px`;
        const elements = cachedElementsRef.current;
        for (let i = 0; i < elements.length; i++) {
          elements[i].style.minWidth = widthStr;
        }
        lastWidth = roundedWidth;
      }
      
      if (roundedScroll !== lastScroll) {
        chart.scrollLeft = roundedScroll;
        lastScroll = roundedScroll;
      }
      
      // Store current scale
      currentScaleRef.current = scale;
      
      if (t < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Animation complete - single React state update
        animationRef.current = null;
        cachedElementsRef.current = null;
        setZoomScale(targetScale);
      }
    };
    
    animationRef.current = requestAnimationFrame(animate);
  }, [chartRef, setZoomScale, tasks, extraRange]);

  return {
    viewPreset,
    extraRange,
    handlePresetChange,
  };
};

export default useGanttPresets;
